/**
 * AdminClubsTab - Club management tab for admin panel
 * Self-contained component for managing clubs
 * Extracted from AdminPage.js for better maintainability
 */
import React, { useState, useEffect, useMemo } from 'react';
import { 
  RefreshCw, Trash2, MapPin, Eye, EyeOff, Star, Shield,
  ChevronLeft, ChevronRight, Pencil, Check, X, Loader2, Globe,
  CheckSquare, Square, ExternalLink, ArrowRightLeft, AlertTriangle,
  Search, User
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Label } from '../ui/label';
import { RowActionsMenu } from './RowActionsMenu';
import { toast } from 'sonner';

import { API_URL } from '../../utils/api';

export function AdminClubsTab({ searchQuery = '', onDataChange }) {
  // State
  const [clubs, setClubs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClubs, setSelectedClubs] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // Slug editing
  const [editingSlug, setEditingSlug] = useState(null);
  const [newSlug, setNewSlug] = useState('');
  const [slugUpdating, setSlugUpdating] = useState(false);
  
  // Verification editing
  const [editingClubVerification, setEditingClubVerification] = useState(null);
  const [verificationData, setVerificationData] = useState({
    verified_accredited: false,
    verified_insurance: false,
    verified_ppl: false,
    verified_safeguarding: false
  });
  const [verificationUpdating, setVerificationUpdating] = useState(false);
  
  // Transfer ownership
  const [transferClub, setTransferClub] = useState(null);
  const [transferNewOwnerEmail, setTransferNewOwnerEmail] = useState('');
  const [transferUserSearch, setTransferUserSearch] = useState('');
  const [transferConfirmed, setTransferConfirmed] = useState(false);
  const [transferring, setTransferring] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const clubsPerPage = 20;

  // Fetch data on mount
  useEffect(() => {
    fetchClubs();
    fetchUsers();
  }, []);

  const fetchClubs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/clubs`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setClubs(data);
      }
    } catch (error) {
      console.error('Error fetching clubs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const generateSlugs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/generate-club-slugs`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || 'Slugs generated successfully');
        fetchClubs();
        if (onDataChange) onDataChange();
      } else {
        toast.error(data.detail || 'Failed to generate slugs');
      }
    } catch (error) {
      toast.error('Error generating slugs');
    }
  };

  const toggleFeatured = async (clubId, currentFeatured) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/clubs/${clubId}/featured`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ featured: !currentFeatured })
      });
      if (response.ok) {
        toast.success(currentFeatured ? 'Club unfeatured' : 'Club featured');
        fetchClubs();
      }
    } catch (error) {
      toast.error('Error updating featured status');
    }
  };

  const toggleVisibility = async (clubId, currentVisible) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/clubs/${clubId}/visibility`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ visible: !currentVisible })
      });
      if (response.ok) {
        toast.success(currentVisible ? 'Club hidden' : 'Club visible');
        fetchClubs();
      }
    } catch (error) {
      toast.error('Error updating visibility');
    }
  };

  const deleteClub = async (clubId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/clubs/${clubId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Club deleted');
        fetchClubs();
        if (onDataChange) onDataChange();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to delete club');
      }
    } catch (error) {
      toast.error('Error deleting club');
    }
  };

  const bulkDeleteClubs = async () => {
    if (selectedClubs.size === 0) return;
    setBulkActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      let deleted = 0;
      for (const clubId of selectedClubs) {
        const response = await fetch(`${API_URL}/api/clubs/${clubId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        if (response.ok) deleted++;
      }
      toast.success(`Deleted ${deleted} clubs`);
      setSelectedClubs(new Set());
      fetchClubs();
      if (onDataChange) onDataChange();
    } catch (error) {
      toast.error('Error deleting clubs');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const updateSlug = async (clubId) => {
    if (!newSlug.trim()) return;
    setSlugUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/clubs/${clubId}/slug`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ slug: newSlug.trim() })
      });
      if (response.ok) {
        toast.success('Slug updated');
        setEditingSlug(null);
        setNewSlug('');
        fetchClubs();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to update slug');
      }
    } catch (error) {
      toast.error('Error updating slug');
    } finally {
      setSlugUpdating(false);
    }
  };

  const openVerificationModal = (club) => {
    setEditingClubVerification(club);
    setVerificationData({
      verified_accredited: club.verified_accredited || false,
      verified_insurance: club.verified_insurance || false,
      verified_ppl: club.verified_ppl || false,
      verified_safeguarding: club.verified_safeguarding || false
    });
  };

  const saveVerifications = async () => {
    if (!editingClubVerification) return;
    setVerificationUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/clubs/${editingClubVerification.club_id}/verifications`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(verificationData)
      });
      if (response.ok) {
        toast.success('Verifications updated');
        setEditingClubVerification(null);
        fetchClubs();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to update verifications');
      }
    } catch (error) {
      toast.error('Error updating verifications');
    } finally {
      setVerificationUpdating(false);
    }
  };

  // Transfer ownership handlers
  const openTransferModal = (club) => {
    setTransferClub(club);
    setTransferNewOwnerEmail('');
    setTransferUserSearch('');
    setTransferConfirmed(false);
  };

  const closeTransferModal = () => {
    setTransferClub(null);
    setTransferNewOwnerEmail('');
    setTransferUserSearch('');
    setTransferConfirmed(false);
  };

  const selectUserForTransfer = (user) => {
    setTransferNewOwnerEmail(user.email);
    setTransferUserSearch('');
  };

  // Filter users for transfer search (exclude current owner)
  const filteredUsersForTransfer = useMemo(() => {
    if (!transferUserSearch || transferUserSearch.length < 2 || !transferClub) return [];
    const search = transferUserSearch.toLowerCase();
    return users
      .filter(u => 
        u.user_id !== transferClub.owner_id &&
        (u.email?.toLowerCase().includes(search) || u.name?.toLowerCase().includes(search))
      )
      .slice(0, 8);
  }, [transferUserSearch, users, transferClub]);

  // Get selected user details for verification display
  const selectedTransferUser = useMemo(() => {
    if (!transferNewOwnerEmail) return null;
    return users.find(u => u.email?.toLowerCase() === transferNewOwnerEmail.toLowerCase());
  }, [transferNewOwnerEmail, users]);

  // Get current owner details
  const currentOwnerDetails = useMemo(() => {
    if (!transferClub) return null;
    return users.find(u => u.user_id === transferClub.owner_id);
  }, [transferClub, users]);

  // Check if transfer is to same owner (prevent accidental same-owner transfer)
  const isTransferToSameOwner = useMemo(() => {
    if (!transferClub || !transferNewOwnerEmail) return false;
    const currentOwner = users.find(u => u.user_id === transferClub.owner_id);
    return currentOwner?.email?.toLowerCase() === transferNewOwnerEmail.toLowerCase();
  }, [transferClub, transferNewOwnerEmail, users]);

  // Validate transfer can proceed
  const canTransfer = transferNewOwnerEmail && 
                      transferConfirmed && 
                      !transferring && 
                      !isTransferToSameOwner &&
                      selectedTransferUser;

  const executeTransfer = async () => {
    if (!transferClub || !transferNewOwnerEmail || !transferConfirmed) return;
    if (isTransferToSameOwner) {
      toast.error('Cannot transfer to the current owner');
      return;
    }
    
    setTransferring(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/clubs/${transferClub.club_id}/transfer`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ new_owner_email: transferNewOwnerEmail.toLowerCase() })
      });
      
      if (response.ok) {
        toast.success(`Club ownership transferred to ${transferNewOwnerEmail}`);
        closeTransferModal();
        fetchClubs();
        if (onDataChange) onDataChange();
      } else {
        const data = await response.json();
        if (response.status === 404) {
          if (data.detail?.includes('owner')) {
            toast.error('No user found with this email address');
          } else {
            toast.error('Club no longer exists');
          }
        } else {
          toast.error(data.detail || 'Failed to transfer ownership');
        }
      }
    } catch (error) {
      toast.error('Error transferring ownership');
    } finally {
      setTransferring(false);
    }
  };

  // Get owner name helper
  const getOwnerName = (ownerId) => {
    const owner = users.find(u => u.user_id === ownerId);
    return owner ? owner.name || owner.email : 'Unknown';
  };

  // Filter clubs
  const filteredClubs = clubs.filter(club => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return club.name?.toLowerCase().includes(query) ||
           club.location?.toLowerCase().includes(query) ||
           club.slug?.toLowerCase().includes(query);
  });

  // Pagination
  const totalPages = Math.ceil(filteredClubs.length / clubsPerPage);
  const paginatedClubs = filteredClubs.slice(
    (currentPage - 1) * clubsPerPage,
    currentPage * clubsPerPage
  );

  // Toggle select all
  const toggleSelectAll = () => {
    if (selectedClubs.size === paginatedClubs.length) {
      setSelectedClubs(new Set());
    } else {
      setSelectedClubs(new Set(paginatedClubs.map(c => c.club_id)));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div>
      {/* Utility Actions Bar */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-4 flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-900">Club Management</h3>
          <p className="text-sm text-slate-500">Manage clubs and generate SEO-friendly URLs</p>
        </div>
        <Button 
          size="sm"
          variant="outline"
          onClick={generateSlugs}
          data-testid="generate-slugs-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Generate URL Slugs
        </Button>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {/* Bulk Actions Bar */}
        {selectedClubs.size > 0 && (
          <div className="bg-violet-50 border-b border-violet-200 px-6 py-3 flex items-center justify-between">
            <span className="text-sm font-medium text-violet-800">
              {selectedClubs.size} club{selectedClubs.size > 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center gap-2">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    size="sm" 
                    variant="destructive"
                    disabled={bulkActionLoading}
                    data-testid="bulk-delete-clubs-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete Selected
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete {selectedClubs.size} Clubs</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete these clubs? This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={bulkDeleteClubs} className="bg-red-600 hover:bg-red-700">
                      Delete All
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <Button 
                size="sm" 
                variant="ghost"
                onClick={() => setSelectedClubs(new Set())}
              >
                Clear Selection
              </Button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="w-12 px-4 py-4">
                  <Checkbox
                    checked={paginatedClubs.length > 0 && selectedClubs.size === paginatedClubs.length}
                    onCheckedChange={toggleSelectAll}
                    data-testid="select-all-clubs"
                  />
                </th>
                <th className="text-left px-3 py-4 text-sm font-semibold text-slate-600">Club</th>
                <th className="text-left px-3 py-4 text-sm font-semibold text-slate-600">Slug</th>
                <th className="text-left px-3 py-4 text-sm font-semibold text-slate-600">Owner</th>
                <th className="text-left px-3 py-4 text-sm font-semibold text-slate-600">Rating</th>
                <th className="text-left px-3 py-4 text-sm font-semibold text-slate-600">Verifications</th>
                <th className="text-left px-3 py-4 text-sm font-semibold text-slate-600">Featured</th>
                <th className="text-left px-3 py-4 text-sm font-semibold text-slate-600">Visible</th>
                <th className="text-left px-3 py-4 text-sm font-semibold text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {paginatedClubs.map((club) => (
                <tr key={club.club_id} className="hover:bg-slate-50">
                  <td className="px-4 py-4">
                    <Checkbox
                      checked={selectedClubs.has(club.club_id)}
                      onCheckedChange={(checked) => {
                        const newSet = new Set(selectedClubs);
                        if (checked) {
                          newSet.add(club.club_id);
                        } else {
                          newSet.delete(club.club_id);
                        }
                        setSelectedClubs(newSet);
                      }}
                    />
                  </td>
                  <td className="px-3 py-4">
                    <div className="flex items-center gap-3">
                      {club.image_url && (
                        <img src={club.image_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                      )}
                      <div>
                        <p className="font-medium text-slate-900">{club.name}</p>
                        <p className="text-sm text-slate-500 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {club.location}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    {editingSlug === club.club_id ? (
                      <div className="flex items-center gap-2">
                        <Input
                          value={newSlug}
                          onChange={(e) => setNewSlug(e.target.value)}
                          className="w-40 h-8 text-sm"
                          placeholder="new-slug"
                        />
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => updateSlug(club.club_id)}
                          disabled={slugUpdating}
                        >
                          {slugUpdating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 text-green-600" />}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => { setEditingSlug(null); setNewSlug(''); }}
                        >
                          <X className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <code className="text-xs bg-slate-100 px-2 py-1 rounded">{club.slug || 'No slug'}</code>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => { setEditingSlug(club.club_id); setNewSlug(club.slug || ''); }}
                        >
                          <Pencil className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-4 text-sm text-slate-600">
                    {getOwnerName(club.owner_id)}
                  </td>
                  <td className="px-3 py-4">
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                      <span className="text-sm">{club.rating?.toFixed(1) || '0.0'}</span>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openVerificationModal(club)}
                      className="text-xs"
                    >
                      <Shield className="w-4 h-4 mr-1" />
                      {[club.verified_accredited, club.verified_insurance, club.verified_ppl, club.verified_safeguarding].filter(Boolean).length}/4
                    </Button>
                  </td>
                  <td className="px-3 py-4">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => toggleFeatured(club.club_id, club.featured)}
                      className={club.featured ? 'text-amber-600' : 'text-slate-400'}
                    >
                      <Star className={`w-4 h-4 ${club.featured ? 'fill-amber-400' : ''}`} />
                    </Button>
                  </td>
                  <td className="px-3 py-4">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => toggleVisibility(club.club_id, club.visible !== false)}
                      className={club.visible !== false ? 'text-green-600' : 'text-slate-400'}
                    >
                      {club.visible !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </Button>
                  </td>
                  <td className="px-3 py-4">
                    {(() => {
                      const groups = [
                        {
                          items: [
                            {
                              label: 'View Club Page',
                              icon: <ExternalLink className="w-4 h-4 text-slate-500" />,
                              onClick: () => window.open(`/clubs/${club.slug || club.club_id}`, '_blank', 'noopener,noreferrer'),
                              testId: `view-club-${club.club_id}`,
                            },
                            {
                              label: 'Edit Club Details',
                              icon: <Pencil className="w-4 h-4 text-violet-500" />,
                              onClick: () => window.open(`/dashboard/edit-club/${club.club_id}`, '_blank', 'noopener,noreferrer'),
                              testId: `edit-club-${club.club_id}`,
                            },
                            {
                              label: 'Transfer Ownership',
                              icon: <ArrowRightLeft className="w-4 h-4 text-blue-600" />,
                              onClick: () => openTransferModal(club),
                              testId: `transfer-club-${club.club_id}`,
                            },
                          ],
                        },
                        {
                          items: [{
                            label: 'Delete Club',
                            icon: <Trash2 className="w-4 h-4" />,
                            destructive: true,
                            onClick: () => deleteClub(club.club_id),
                            testId: `delete-club-${club.club_id}`,
                            confirm: {
                              title: 'Delete Club',
                              description: `Are you sure you want to delete "${club.name}"? This action cannot be undone.`,
                              actionLabel: 'Delete',
                            },
                          }],
                        },
                      ];
                      return <RowActionsMenu groups={groups} triggerTestId={`manage-club-${club.club_id}`} />;
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-slate-200 flex items-center justify-between">
            <span className="text-sm text-slate-500">
              Showing {(currentPage - 1) * clubsPerPage + 1} to {Math.min(currentPage * clubsPerPage, filteredClubs.length)} of {filteredClubs.length}
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Verification Modal */}
      <Dialog open={!!editingClubVerification} onOpenChange={() => setEditingClubVerification(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Verifications - {editingClubVerification?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
              <Checkbox
                checked={verificationData.verified_accredited}
                onCheckedChange={(checked) => setVerificationData(prev => ({ ...prev, verified_accredited: checked }))}
              />
              <div>
                <p className="font-medium text-slate-900">Accredited</p>
                <p className="text-sm text-slate-500">Club is officially accredited</p>
              </div>
            </label>
            <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
              <Checkbox
                checked={verificationData.verified_insurance}
                onCheckedChange={(checked) => setVerificationData(prev => ({ ...prev, verified_insurance: checked }))}
              />
              <div>
                <p className="font-medium text-slate-900">Insurance</p>
                <p className="text-sm text-slate-500">Has valid insurance coverage</p>
              </div>
            </label>
            <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
              <Checkbox
                checked={verificationData.verified_ppl}
                onCheckedChange={(checked) => setVerificationData(prev => ({ ...prev, verified_ppl: checked }))}
              />
              <div>
                <p className="font-medium text-slate-900">PPL License</p>
                <p className="text-sm text-slate-500">Has PPL music license</p>
              </div>
            </label>
            <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
              <Checkbox
                checked={verificationData.verified_safeguarding}
                onCheckedChange={(checked) => setVerificationData(prev => ({ ...prev, verified_safeguarding: checked }))}
              />
              <div>
                <p className="font-medium text-slate-900">Safeguarding</p>
                <p className="text-sm text-slate-500">Safeguarding policy in place</p>
              </div>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingClubVerification(null)}>
              Cancel
            </Button>
            <Button onClick={saveVerifications} disabled={verificationUpdating}>
              {verificationUpdating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Verifications
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Ownership Modal */}
      <Dialog open={!!transferClub} onOpenChange={(open) => !open && closeTransferModal()}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto" data-testid="transfer-ownership-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-blue-600" />
              Transfer Club Ownership
            </DialogTitle>
            <DialogDescription>
              Transfer management rights for this club to another registered user.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-5 py-4">
            {/* Club being transferred */}
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <Label className="text-xs text-slate-500 uppercase tracking-wide">Club Being Transferred</Label>
              <p className="font-semibold text-slate-900 mt-1">{transferClub?.name}</p>
              {transferClub?.location && (
                <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5">
                  <MapPin className="w-3 h-3" /> {transferClub.location}
                </p>
              )}
            </div>

            {/* Current Owner */}
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <Label className="text-xs text-amber-700 uppercase tracking-wide">Current Owner</Label>
              <div className="flex items-center gap-2 mt-1">
                <User className="w-4 h-4 text-amber-600" />
                <div>
                  <p className="font-medium text-slate-900">
                    {currentOwnerDetails?.name || 'Unknown'}
                  </p>
                  <p className="text-sm text-slate-600">
                    {currentOwnerDetails?.email || 'No email'}
                  </p>
                </div>
              </div>
            </div>

            {/* New Owner Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Select New Owner</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name or email..."
                  value={transferUserSearch}
                  onChange={(e) => setTransferUserSearch(e.target.value)}
                  className="pl-9"
                  data-testid="transfer-user-search"
                />
              </div>
              
              {/* User search results dropdown */}
              {filteredUsersForTransfer.length > 0 && (
                <div className="border border-slate-200 rounded-lg shadow-sm max-h-48 overflow-y-auto">
                  {filteredUsersForTransfer.map((user) => (
                    <button
                      key={user.user_id}
                      type="button"
                      onClick={() => selectUserForTransfer(user)}
                      className="w-full px-3 py-2 text-left hover:bg-slate-50 border-b border-slate-100 last:border-b-0 flex items-center gap-2"
                      data-testid={`select-user-${user.user_id}`}
                    >
                      <User className="w-4 h-4 text-slate-400" />
                      <div>
                        <p className="text-sm font-medium text-slate-900">{user.name || user.email}</p>
                        <p className="text-xs text-slate-500">{user.email}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {transferUserSearch.length >= 2 && filteredUsersForTransfer.length === 0 && (
                <p className="text-sm text-slate-500 py-2">No users found matching "{transferUserSearch}"</p>
              )}
            </div>

            {/* Selected New Owner Verification */}
            {selectedTransferUser && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <Label className="text-xs text-blue-700 uppercase tracking-wide">New Owner (Selected)</Label>
                <div className="flex items-center gap-2 mt-1">
                  <User className="w-4 h-4 text-blue-600" />
                  <div>
                    <p className="font-medium text-slate-900">
                      {selectedTransferUser.name || 'No name'}
                    </p>
                    <p className="text-sm text-slate-600">
                      {selectedTransferUser.email}
                    </p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="mt-2 text-xs text-blue-600"
                  onClick={() => setTransferNewOwnerEmail('')}
                >
                  Change selection
                </Button>
              </div>
            )}

            {/* Same owner warning */}
            {isTransferToSameOwner && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5" />
                <p className="text-sm text-red-700">
                  Cannot transfer to the current owner. Please select a different user.
                </p>
              </div>
            )}

            {/* Instructor cascade notice */}
            <div className="p-3 bg-slate-100 border border-slate-200 rounded-lg">
              <p className="text-xs text-slate-600">
                <strong>Note:</strong> Instructors linked to this club and owned by the current owner will also be transferred to the new owner.
              </p>
            </div>

            {/* Confirmation checkbox */}
            <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
              <Checkbox
                checked={transferConfirmed}
                onCheckedChange={(checked) => setTransferConfirmed(checked)}
                disabled={!selectedTransferUser || isTransferToSameOwner}
                data-testid="transfer-confirmation-checkbox"
              />
              <div>
                <p className="font-medium text-slate-900 text-sm">I confirm this transfer</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  I understand this action transfers all club management rights to the selected user.
                </p>
              </div>
            </label>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeTransferModal} disabled={transferring}>
              Cancel
            </Button>
            <Button 
              onClick={executeTransfer} 
              disabled={!canTransfer}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="execute-transfer-btn"
            >
              {transferring ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Transferring...
                </>
              ) : (
                <>
                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                  Transfer Ownership
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminClubsTab;
